import { requestOtpAuth as requestOtp } from '@api/generated/sdk.gen';
import { useLogin } from '@refinedev/core';
import { parseApiError } from '@shared/lib/api-error';
import { maskPhone } from '@shared/lib/mask-phone';
import { AuthShell } from '@shared/ui/AuthShell';
import { Alert, Button, Form, Input, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

const { Text } = Typography;

type FormValues = {
  code: string;
};

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Страница подтверждения OTP.
 *
 * Принимает телефон через `location.state.phone` (от `LoginPage`). Если
 * пользователь зашёл напрямую — редиректит обратно на `/login`. После
 * успешной верификации через Refine `useLogin` происходит редирект на `/`.
 */
export function VerifyOtpPage() {
  const { t } = useTranslation(['auth', 'errors']);
  const navigate = useNavigate();
  const location = useLocation();
  const phone = (location.state as { phone?: string } | null)?.phone;

  const { mutate: login, isPending } = useLogin<{
    phone: string;
    code: string;
    deviceName: string;
  }>();

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (!phone) {
      void navigate('/login', { replace: true });
    }
  }, [phone, navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => {
      setResendIn((s) => Math.max(0, s - 1));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [resendIn]);

  if (!phone) return <></>;

  const onSubmit = (values: FormValues): void => {
    setErrorCode(null);
    login(
      { phone, code: values.code, deviceName: t('auth:verify.deviceName') },
      {
        onSuccess: (result) => {
          if (!result.success && result.error) {
            setErrorCode(result.error.message || 'UNKNOWN_ERROR');
          }
        },
        onError: (err) => {
          setErrorCode((err as { message?: string }).message ?? 'UNKNOWN_ERROR');
        },
      },
    );
  };

  const onResend = async (): Promise<void> => {
    setErrorCode(null);
    try {
      await requestOtp({ body: { phone } });
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch (err: unknown) {
      const apiErr = parseApiError(
        (err as { response?: { data?: unknown } }).response?.data,
        (err as { response?: { status?: number } }).response?.status,
      );
      setErrorCode(apiErr.code);
    }
  };

  return (
    <AuthShell
      title={t('auth:verify.title')}
      subtitle={t('auth:verify.subtitle', { phone: maskPhone(phone) })}
      footer={
        <Space size="middle">
          <Link to="/login" style={{ color: '#475569', fontSize: 13 }}>
            ← {t('auth:verify.changeNumber')}
          </Link>
        </Space>
      }
    >
      {errorCode && (
        <Alert
          type="error"
          showIcon
          message={t(`errors:${errorCode}`, { defaultValue: t('errors:UNKNOWN_ERROR') })}
          style={{ marginBottom: 20 }}
          closable
          onClose={() => {
            setErrorCode(null);
          }}
        />
      )}

      <Form<FormValues> layout="vertical" onFinish={onSubmit} requiredMark={false}>
        <Form.Item
          label={t('auth:verify.codeLabel')}
          name="code"
          rules={[
            { required: true, message: t('errors:fields.NOT_BLANK') },
            {
              pattern: /^\d{4,8}$/,
              message: t('errors:OTP_INVALID', { attemptsLeft: '—' }),
            },
          ]}
        >
          <Input
            placeholder={t('auth:verify.codePlaceholder')}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            size="large"
            maxLength={8}
            style={{
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.4em',
              textAlign: 'center',
              fontSize: 22,
              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            }}
          />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={isPending} block size="large">
          {t('auth:verify.submit')}
        </Button>

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          {resendIn > 0 ? (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('auth:verify.resendIn', { seconds: resendIn })}
            </Text>
          ) : (
            <Button type="link" onClick={() => void onResend()} style={{ padding: 0 }}>
              {t('auth:verify.resend')}
            </Button>
          )}
        </div>
      </Form>
    </AuthShell>
  );
}
